import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import Api from '../../../../../component/Api';
import { toast } from 'react-toastify';

export const useMarkSubmission = (
    studentId,
    studentData,
    onBack,
    onSubmitSuccess,
    onRefresh,
    onStatusUpdate,
    internshipStatus,
    attendanceStatus,
) => {
    const { http } = useMemo(() => Api(), []);
    const [cca, setCcaMark] = useState('');
    const [ese, setEseMark] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const originalValues = useRef({ cca: '', ese: '' });

    useEffect(() => {
        if (studentData) {
            const ccaVal = studentData.cca != null ? studentData.cca.toString() : '';
            const eseVal = studentData.ese != null ? studentData.ese.toString() : '';
            setCcaMark(ccaVal);
            setEseMark(eseVal);
            originalValues.current = { cca: ccaVal, ese: eseVal };
        }
    }, [studentData]);

    const validateMarks = useCallback(() => {
        const errors = [];
        if (attendanceStatus === 'absent') return errors;

        if (cca) {
            const v = parseFloat(cca);
            const max = parseFloat(studentData.cca_max) || 100;
            if (isNaN(v)) errors.push('CCA mark must be a valid number');
            else if (v < 0) errors.push('CCA mark cannot be negative');
            else if (v > max) errors.push(`CCA mark cannot exceed ${max}`);
        }
        if (ese) {
            const v = parseFloat(ese);
            const max = parseFloat(studentData.ese_max) || 100;
            if (isNaN(v)) errors.push('ESE mark must be a valid number');
            else if (v < 0) errors.push('ESE mark cannot be negative');
            else if (v > max) errors.push(`ESE mark cannot exceed ${max}`);
        }
        if (!cca || !ese) errors.push('Please enter both CCA and ESE marks');
        return errors;
    }, [cca, ese, studentData, attendanceStatus]);

    const handleSubmit = useCallback(async () => {
        const errors = validateMarks();
        if (errors.length > 0) {
            await Swal.fire({
                title: '<span style="font-size:1rem;font-weight:700;color:#111827">Validation Failed</span>',
                html: `<div style="text-align:left;margin-top:4px">${errors.map(e =>
                    `<div style="display:flex;align-items:center;gap:8px;padding:5px 0;border-bottom:1px solid #f3f4f6;font-size:0.85rem;color:#374151">
                        <span style="color:#dc3545;flex-shrink:0">&#9679;</span>${e}
                    </div>`
                ).join('')}</div>`,
                icon: 'error',
                confirmButtonColor: '#581f37',
                confirmButtonText: 'OK',
                customClass: { popup: 'rounded-3' },
            });
            return;
        }

        const pill = (text, bg, color) =>
            `<span style="display:inline-block;padding:2px 9px;border-radius:20px;background:${bg};color:${color};font-size:0.75rem;font-weight:600">${text}</span>`;

        const attendancePill = attendanceStatus === 'present'
            ? pill('Present', '#d1fae5', '#065f46')
            : pill('Absent', '#fee2e2', '#991b1b');

        const internshipPill = internshipStatus === 'complete'
            ? pill('Complete', '#d1fae5', '#065f46')
            : pill('Pending', '#fef3c7', '#92400e');

        const cell = (label, value) => `
            <div style="flex:1;min-width:0;padding:10px 14px;background:#fff;border-radius:7px;border:1px solid #e5e7eb;text-align:left">
                <div style="font-size:0.68rem;color:#9ca3af;font-weight:600;margin-bottom:4px;text-transform:uppercase;letter-spacing:0.05em">${label}</div>
                <div style="font-size:0.9rem;font-weight:700;color:#111827;line-height:1.3">${value}</div>
            </div>`;

        const result = await Swal.fire({
            title: '<span style="font-size:1rem;font-weight:700;color:#111827">Review & Confirm</span>',
            html: `
                <div style="text-align:left;margin-top:4px">

                    <div style="font-size:0.82rem;color:#374151;margin-bottom:14px;line-height:1.7;display:flex;flex-direction:column;gap:4px;">
                        <div>
                            <span style="color:#9ca3af">Student</span>&nbsp;
                            <strong style="color:#111827">${studentData.student}</strong>
                        </div>
                        <div>
                            <span style="color:#9ca3af">Course</span>&nbsp;
                            <strong style="color:#111827">${studentData.course}</strong>
                        </div>
                    </div>

                    ${attendanceStatus !== 'absent' ? `
                        <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap">
                            ${cell('CCA', `${cca || '—'} <span style="font-size:0.75rem;color:#9ca3af;font-weight:400">/ ${studentData.cca_max || 'N/A'}</span>`)}
                            ${cell('ESE', `${ese || '—'} <span style="font-size:0.75rem;color:#9ca3af;font-weight:400">/ ${studentData.ese_max || 'N/A'}</span>`)}
                        </div>`
                    : ''}

                    <div style="display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap">
                        ${cell('Internship', internshipPill)}
                        ${cell('Attendance', attendancePill)}
                    </div>

                    <p style="margin:0;font-size:0.74rem;color:#9ca3af;text-align:center">
                        Confirm to save these permanently.
                    </p>
                </div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#581f37',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Save',
            cancelButtonText: 'Cancel',
            width: 480,
            customClass: { popup: 'rounded-3' },
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            preConfirm: async () => {
                try {
                    setIsSubmitting(true);
                    const isAbsent = attendanceStatus === 'absent';
                    const payload = {
                        student_id: studentId ? parseInt(studentId, 10) : null,
                        cca_mark: isAbsent ? null : (cca ? parseFloat(cca) : null),
                        ese_mark: isAbsent ? null : (ese ? parseFloat(ese) : null),
                        internship_status: internshipStatus ?? null,
                        attendance_status: attendanceStatus ?? null,
                    };
                    const response = await http.post('/is/markSubmit', payload);
                    onStatusUpdate?.('draft');
                    return response;
                } catch (error) {
                    Swal.showValidationMessage(
                        error?.response?.data?.message || 'Failed to save. Please try again.'
                    );
                } finally {
                    setIsSubmitting(false);
                }
            },
        });

        if (!result.isConfirmed) return;

        originalValues.current = { cca, ese };

        toast.success('Saved');

        onSubmitSuccess?.();
        //eslint-disable-next-line
    }, [validateMarks, studentData, cca, ese, studentId, http, onSubmitSuccess, onStatusUpdate, internshipStatus, attendanceStatus]);

    const handleCancel = useCallback(() => {
        const hasChanges = cca !== originalValues.current.cca || ese !== originalValues.current.ese;
        if (hasChanges) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure you want to go back?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Yes, Discard',
                cancelButtonText: 'Stay',
            }).then(r => { if (r.isConfirmed) onBack(); });
        } else {
            onBack();
        }
    }, [cca, ese, onBack]);

    const handleCcaChange = useCallback((e) => {
        const v = e.target.value;
        if (v === '' || (!isNaN(v) && parseFloat(v) >= 0)) setCcaMark(v);
    }, []);

    const handleEseChange = useCallback((e) => {
        const v = e.target.value;
        if (v === '' || (!isNaN(v) && parseFloat(v) >= 0)) setEseMark(v);
    }, []);

    return { cca, ese, isSubmitting, handleSubmit, handleCancel, handleCcaChange, handleEseChange };
};